import React, { useEffect, useState } from 'react';
import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import Input from 'b2b-ui-components-lib/lib/components/input';
import Button from 'b2b-ui-components-lib/lib/components/button';
import TextArea from 'b2b-ui-components-lib/lib/components/textarea';
import { gstinValidator, isFormValid } from 'common/utils/validator';
import { getDefaultValue, KEYS_TO_IGNORE } from './formControl';
import { fetchCityStateList, fetchGSTDetail, validateGstNumber } from 'common/services/http.service';
import Select from 'b2b-ui-components-lib/lib/components/select';
import { isOrderConfirmationUrl, prepareOptionsForDropDown } from 'dedicated/services/summary.service';
import { addressRegexValidator, removeAsciiCode } from 'common/utils/app.utils';
import { ORDER_STATUS } from 'home/view/upgrade-order/constants';
import ShippingDetails from '../shippingDetails/shippingDetails';
import { ORDER_TYPE } from 'common/constants/app.constants';

const TitleComponent = React.memo(() => {
  return (
    <div className="upgrade-header-open">
      <div className="title pad-bottom-0">Billing Details</div>
      <label className="field_label">Confirm billing details for shifting order.</label>
    </div>
  );
});

const BillingDetails = ({
  order,
  updateParentForm,
  setFormStatus,
  leadStatus,
  onCancelClick,
  isCancelDisabled,
  loading,
  onSubmitClick,
  isSubmitDisabled,
  formData,
  isOrderConfirmation
}) => {
  const [cityList, setCityList] = useState([]);
  const [form, setForm] = useState(() => getDefaultValue(order));
  const [showLoader, setShowLoader] = useState(false);

  const onCityChange = (value) => setForm({ ...form, city: { ...form.city, value, touched: true } });

  const onAddressChange = (value) => {
    if ((value.length >= 1 && value[value.length - 1] === '') || addressRegexValidator(value)) {
      setForm({ ...form, address: { ...form.address, value, touched: true, errors: form.address.validator(value) } });
    }
  };

  // const onAddressChange = (value) => {
  //   setForm({ ...form, address: { ...form.address, value, touched: true, errors: form.address.validator(value) } });
  // };

  const onPersonNameChange = (value) => setForm({ ...form, personName: { ...form.personName, value, touched: true, errors: form.personName.validator(value) } });

  const onEmailChange = (value) => setForm({ ...form, email: { ...form.email, value, touched: true, errors: form.email.validator(value) } });

  const onMobileNoChange = (value) => setForm({ ...form, mobileNumber: { ...form.mobileNumber, value, touched: true, errors: form.mobileNumber.validator(value) } });

  const onPinCodeChange = (value) => {
    setForm({
      ...form,
      pinCode: { ...form.pinCode, value, touched: true, errors: form.pinCode.validator(value) },
      city: { ...form.city, value: '', touched: true },
      address: { ...form.address, value: '', touched: true }
    });
  };

  const isGSTFieldEmpty = (value) => {
    if (value === '' || value === null) {
      setForm({ ...form, gstin: { value, touched: true, errors: [] } });
      return true;
    }
    return false;
  };
  const onGSTInChange = (value) => {
    value = value.trim().toUpperCase();
    if (gstinValidator(value)) {
      fetchGSTDetail(value).then(
        ({
          data: {
            result: { dty, sez }
          }
        }) => {
          let gstValue = form && form.gstin && form.gstin.value;
          setShowLoader(false);
          if (gstValue !== value) {
            setForm({ ...form, gstin: { value, touched: true, errors: [] } });
          } else {
            setForm({ ...form, gstin: { value, touched: true, errors: [] } });
          }
        },
        (err) => {
          let errMsg = err && err.response && err.response.data && err.response.data.errorMsg;
          setForm({ ...form, gstin: { value, touched: true, errors: [errMsg ? errMsg : 'Please enter valid GSTIN'] } });
          setShowLoader(false);
        }
      );
    } else {
      if (!isGSTFieldEmpty(value)) {
        setForm({ ...form, gstin: { value, touched: true, errors: ['Please enter valid GSTIN'] } });
      }
    }
  };

  const validateGSTIN = (value) => {
    value = value && value.trim().toUpperCase();
    if (gstinValidator(value)) {
      setForm({ ...form, gstin: { value, touched: true, errors: [] } });
      setShowLoader(true);
      validateGstNumber(value).then(
        ({ data: { result } }) => {
          onGSTInChange(value);
        },
        (err) => {
          let errMsg = err && err.response && err.response.data && err.response.data.errorMsg;
          setForm({ ...form, gstin: { value, touched: true, errors: [errMsg ? errMsg : 'Please enter valid GSTIN'] } });
          setShowLoader(false);
        }
      );
    } else {
      if (!isGSTFieldEmpty(value)) {
        setForm({ ...form, gstin: { value, touched: true, errors: ['Please enter valid GSTIN'] } });
      }
    }
  };

  const onFieldBlur = (formElement, keyName) => {
    const { value, validator, errors: existingError } = formElement;
    let errors = [];
    if (typeof validator === 'function') {
      errors = validator(value);
    }
    setForm({ ...form, [keyName]: { ...form[keyName], value, errors: errors?.concat(existingError), touched: true } });
  };

  // const isOrderConfirmationPending = () => {
  //   return order.orderStatus === ORDER_TYPE.ORDER_CONFIRMATION_PENDING;
  // };

  const isDisabled = () => {
    return isOrderConfirmationUrl() || isOrderConfirmation;
  };

  useEffect(() => {
    if (form.pinCode.errors.length === 0 && form.pinCode.value) {
      fetchCityStateList(form.pinCode.value).then(
        ({ data: { result } }) => {
          if (Array.isArray(result)) {
            const cityList = prepareOptionsForDropDown(result, 'city');
            setCityList(cityList);
            if (cityList.length === 1) {
              onCityChange(cityList[0]);
            }
          }
        },
        (err) => {
          let errMsg = err && err.response && err.response.data && err.response.data.errorMsg;
          setCityList([]);
          setForm({
            ...form,
            pinCode: { ...form.pinCode, touched: true, errors: [errMsg ? errMsg : 'Please enter valid Pincode'] },
            city: { ...form.city, value: '', touched: true },
            address: { ...form.address, value: '', touched: true }
          });
          console.error(err);
        }
      );
    }
  }, [form.pinCode.value]);

  useEffect(() => {
    setForm((prev) => {
      const form = getDefaultValue(order);
      if (form && form.gstin && form.gstin.value) {
        validateGSTIN(form.gstin.value);
      }
      return { ...form };
    });
  }, [order]);

  useEffect(() => {
    let formattedForm = {};
    if (form?.address?.value) {
      formattedForm = { ...form, address: { value: removeAsciiCode(form?.address?.value) } };
    }

    if (isFormValid(form, KEYS_TO_IGNORE)) {
      updateParentForm({ billingDetails: formattedForm });
      setFormStatus((formStatus) => ({
        ...formStatus,
        billingDetails: true
      }));
    } else {
      updateParentForm({ billingDetails: formattedForm });
      setFormStatus((formStatus) => ({
        ...formStatus,
        billingDetails: false
      }));
    }
  }, [form]);

  return (
    <div className="upgrade-accordion">
      <Accordion autoScrollOnExpand={order && order.orderStatus !== ORDER_STATUS.OV_REJECTED ? false : true} title={<TitleComponent />} isExpanded={true} variant="round">
        <div className="accordion_body">
          <div className="form_area">
            <div className="devider_group">
              <div className="form_area">
                <h6>Billing Details</h6>
                <React.Fragment>
                  <div className="row">
                    <div className="flg-lg-12">
                      <div className="input_with_loader">
                        <Input
                          label="GST Details"
                          value={form.gstin.value}
                          onChange={validateGSTIN}
                          onBlur={() => onFieldBlur(form.gstin, 'gstin')}
                          errors={form.gstin.errors}
                          touched={form.gstin.touched}
                          maxlength={15}
                          disabled={isDisabled()}
                        />
                        {showLoader && <span className="loading_icon"></span>}
                      </div>
                    </div>
                  </div>

                  <div className="row field_row">
                    <div className="flg-lg-6 flg-md-6">
                      <Input
                        label="Billing Address Pin Code"
                        maxlength={6}
                        value={form.pinCode.value ? form.pinCode.value : ''}
                        isValidChange={(value) => /^(\s*|\d+)$/.test(value)}
                        onBlur={() => onFieldBlur(form.pinCode, 'pinCode')}
                        required
                        errors={form.pinCode.errors}
                        touched={form.pinCode.touched}
                        onChange={onPinCodeChange}
                        disabled={isDisabled()}
                      />
                    </div>
                    <div className="flg-lg-6 flg-md-6">
                      <Select label="Billing Address City" value={form.city.value} onChange={onCityChange} options={cityList} placeholder="City" required disabled={isDisabled()} />
                    </div>
                  </div>
                  <div className="row field_row">
                    <div className="flg-lg-12">
                      <TextArea
                        label="Billing Address"
                        maxlength={250}
                        required
                        value={form.address.value}
                        onBlur={() => onFieldBlur(form.address, 'address')}
                        errors={form.address.errors}
                        touched={form.address.touched}
                        onChange={onAddressChange}
                        disabled={isDisabled()}
                      />
                    </div>
                    <div style={{ position: 'relative' }}>
                      <small id="helpId" className="help-text-counter">
                        {(form.address.value && form.address.value.length) || 0} /250
                      </small>
                    </div>
                  </div>
                </React.Fragment>
              </div>
            </div>
          </div>

          <div className="form_area">
            <h6>Billing Contact Details</h6>
            <div className="row field_row">
              <div className="flg-lg-6 flg-md-6">
                <Input
                  label="Contact Person Name"
                  maxlength={150}
                  required
                  value={form.personName.value}
                  onBlur={() => onFieldBlur(form.personName, 'personName')}
                  errors={form.personName.errors}
                  touched={form.personName.touched}
                  onChange={onPersonNameChange}
                  disabled={isDisabled()}
                />
              </div>
              <div className="flg-lg-6 flg-md-6">
                <Input
                  label="Contact Person Number"
                  maxlength={10}
                  required
                  isValidChange={(value) => /^(\s*|\d+)$/.test(value)}
                  value={form.mobileNumber.value ? form.mobileNumber.value : ''}
                  onBlur={() => onFieldBlur(form.mobileNumber, 'mobileNumber')}
                  errors={form.mobileNumber.errors}
                  touched={form.mobileNumber.touched}
                  onChange={onMobileNoChange}
                  disabled={isDisabled()}
                />
              </div>
            </div>
            <div className="row field_row">
              <div className="flg-lg-6 flg-md-6">
                <Input
                  label="Contact Person Email"
                  required
                  value={form.email.value}
                  onBlur={() => onFieldBlur(form.email, 'email')}
                  errors={form.email.errors}
                  touched={form.email.touched}
                  onChange={onEmailChange}
                  disabled={isDisabled()}
                />
              </div>
            </div>
          </div>
        </div>
        <ShippingDetails order={order} updateParentForm={updateParentForm} setFormStatus={setFormStatus} formData={formData} isOrderConfirmation={isOrderConfirmation} />
        {/* {!(isOrderConfirmation || isOrderConfirmationUrl()) && (
          <ShippingDetails order={order} updateParentForm={updateParentForm} setFormStatus={setFormStatus} formData={formData} />
        )} */}
        {/* <div className="action_button">
          <Button variant="outlinebtn" displayName="Cancel" onClick={onCancelClick} disabled={isCancelDisabled()}></Button>
          <Button displayName="Next" disabled={loading || isSubmitDisabled()} onClick={onSubmitClick}>
            {loading && <span className="loading_icon"></span>}
          </Button>
        </div> */}
      </Accordion>
    </div>
  );
};

export default BillingDetails;
